import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Header from '../../components/navigation/Header';
import Breadcrumb from '../../components/navigation/Breadcrumb';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import TournamentSetupTab from './components/TournamentSetupTab';
import TeamManagementTab from './components/TeamManagementTab';
import ScheduleConfigTab from './components/ScheduleConfigTab';
import PredictionSettingsTab from './components/PredictionSettingsTab';
import { tournamentsService } from '../../services/supabaseService';

const TournamentManagement = () => {
  const { tournamentId } = useParams();
  const [activeTab, setActiveTab] = useState('setup');
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [tournamentData, setTournamentData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const isEditMode = tournamentId && tournamentId !== 'new';

  useEffect(() => {
    if (tournamentId && tournamentId !== 'new') {
      fetchTournamentData();
    }
  }, [tournamentId]);

  const fetchTournamentData = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await tournamentsService?.getById(tournamentId);
      if (data) {
        setTournamentData(data);
      } else {
        setError('Tournament not found');
      }
    } catch (err) {
      console.error('Error fetching tournament:', err);
      setError('Failed to load tournament data');
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'setup', label: 'Tournament Setup', icon: 'Settings' },
    { id: 'teams', label: 'Team Management', icon: 'Users' },
    { id: 'schedule', label: 'Schedule Configuration', icon: 'Calendar' },
    { id: 'predictions', label: 'Prediction Settings', icon: 'Target' }
  ];

  const handleSaveTournament = async (data) => {
    console.log('Saving tournament:', data);
    try {
      if (isEditMode) {
        await tournamentsService?.update(tournamentId, data);
      } else {
        await tournamentsService?.create(data);
      }
      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 3000);
      if (isEditMode) {
        fetchTournamentData();
      }
    } catch (err) {
      console.error('Error saving tournament:', err);
      setError('Failed to save tournament');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <Breadcrumb />
        <main className="main-content">
          <div className="max-w-[1440px] mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                <p className="text-muted-foreground">Loading tournament data...</p>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <Breadcrumb />
      <main className="main-content">
        <div className="max-w-[1440px] mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">
          <div className="mb-6 md:mb-8">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
              <div>
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-2">
                  {isEditMode ? 'Edit Tournament' : 'Create New Tournament'}
                </h1>
                <p className="text-sm md:text-base text-muted-foreground">
                  {isEditMode ? 'Update tournament settings and configuration' : 'Create and configure PowerPlay tournaments'}
                </p>
              </div>
              <div className="flex gap-2 w-full sm:w-auto">
                <Button
                  variant="outline"
                  iconName="Copy"
                  iconPosition="left"
                  className="flex-1 sm:flex-initial"
                >
                  Duplicate
                </Button>
                <Button
                  variant="outline"
                  iconName="FileText"
                  iconPosition="left"
                  className="flex-1 sm:flex-initial"
                >
                  Template
                </Button>
              </div>
            </div>

            {showSuccessMessage && (
              <div className="bg-success/10 border border-success/20 rounded-lg p-4 flex items-center gap-3">
                <Icon name="CheckCircle" size={20} className="text-success flex-shrink-0" />
                <p className="text-sm text-success">
                  Tournament {isEditMode ? 'updated' : 'created'} successfully!
                </p>
              </div>
            )}

            {error && (
              <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 flex items-center gap-3">
                <Icon name="AlertCircle" size={20} className="text-destructive flex-shrink-0" />
                <p className="text-sm text-destructive">{error}</p>
              </div>
            )}
          </div>

          <div className="bg-card border border-border rounded-lg overflow-hidden">
            <div className="border-b border-border overflow-x-auto">
              <div className="flex min-w-max">
                {tabs?.map((tab) => (
                  <button
                    key={tab?.id}
                    onClick={() => setActiveTab(tab?.id)}
                    className={`flex items-center gap-2 px-4 md:px-6 py-3 md:py-4 text-sm md:text-base font-medium transition-colors whitespace-nowrap ${
                      activeTab === tab?.id
                        ? 'text-primary border-b-2 border-primary bg-primary/5' :'text-muted-foreground hover:text-foreground hover:bg-muted'
                    }`}
                  >
                    <Icon name={tab?.icon} size={18} />
                    <span className="hidden sm:inline">{tab?.label}</span>
                    <span className="sm:hidden">{tab?.label?.split(' ')?.[0]}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="p-4 md:p-6 lg:p-8">
              <div className={activeTab === 'setup' ? 'block' : 'hidden'}>
                <TournamentSetupTab 
                  onSave={handleSaveTournament} 
                  initialData={tournamentData}
                  isEditMode={isEditMode}
                />
              </div>
              <div className={activeTab === 'teams' ? 'block' : 'hidden'}>
                <TeamManagementTab tournamentId={tournamentId || '1'} />
              </div>
              <div className={activeTab === 'schedule' ? 'block' : 'hidden'}>
                <ScheduleConfigTab tournamentId={tournamentId || '1'} />
              </div>
              <div className={activeTab === 'predictions' ? 'block' : 'hidden'}>
                <PredictionSettingsTab tournamentId={tournamentId || '1'} />
              </div>
            </div>
          </div>

          <div className="mt-6 md:mt-8 bg-muted rounded-lg p-4 md:p-6">
            <div className="flex items-start gap-3">
              <Icon name="Info" size={20} className="text-primary flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-semibold text-foreground mb-2">Quick Tips</h3>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• Tournament predictions auto-enable 7 days before start date</li>
                  <li>• Match predictions lock 1 hour before match start time</li>
                  <li>• Points are calculated automatically when results are posted</li>
                  <li>• Use templates to quickly create similar tournaments</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default TournamentManagement;